import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  useRecordContext,
  BooleanField,
  Link,
  Edit,
  TextInput,
  FormTab,
  TabbedForm,
  Labeled,
  SingleFieldList,
  ArrayField,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";

import { AvatarField as UserAvatarField, UserLinkField } from "./users";

import { WorkspaceLinkField } from "./workspaces";
import {
  Avatar,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField as MUITextField,
} from "@mui/material";

const formatChannelDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CHANNEL_MEMBER_ROLES = [
  { id: "owner", name: "Owner" },
  { id: "admin", name: "Admin" },
  { id: "creator", name: "Creator" },
  { id: "member", name: "Member" },
  { id: "guest", name: "Guest" },
];

const CHANNEL_MEMBER_STATUSES = [
  { id: "active", name: "Active" },
  { id: "user_left", name: "User left" },
  { id: "removed_from_channel", name: "Removed from channel" },
  { id: "removed_from_workspace", name: "Removed from workspace" },
];

const normalizeMemberRole = (r) =>
  r && CHANNEL_MEMBER_ROLES.some((c) => c.id === r) ? r : "member";

const normalizeMemberStatus = (s) =>
  s && CHANNEL_MEMBER_STATUSES.some((c) => c.id === s) ? s : "active";

const normalizeSortValue = (value) => (value || "").toString().trim().toLowerCase();

const ChannelMemberEditorRow = ({ channelId, member }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const serverRole = normalizeMemberRole(member.role);
  const serverStatus = normalizeMemberStatus(member.status);
  const [role, setRole] = React.useState(serverRole);
  const [status, setStatus] = React.useState(serverStatus);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setRole(normalizeMemberRole(member.role));
    setStatus(normalizeMemberStatus(member.status));
  }, [member.user_id, member.role, member.status]);

  const roleDirty = role !== serverRole;
  const statusDirty = status !== serverStatus;
  const hasChanges = roleDirty || statusDirty;

  const handleApply = async () => {
    const patch = {};
    if (roleDirty) patch.role = role;
    if (statusDirty) patch.status = status;
    if (Object.keys(patch).length === 0) return;
    setLoading(true);
    try {
      await dataProvider.updateChannelMemberAdmin({
        channelId,
        userId: member.user_id,
        ...patch,
      });
      notify("Member updated", { type: "success" });
      refresh();
    } catch (e) {
      notify(
        typeof e?.message === "string" ? e.message : "Failed to update member",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableRow hover>
      <TableCell sx={{ minWidth: 160, maxWidth: 220, verticalAlign: "middle" }}>
        <ReferenceField record={member} source="user_id" reference="users">
          <UserLinkField />
        </ReferenceField>
      </TableCell>
      <TableCell sx={{ minWidth: 140, verticalAlign: "middle" }}>
        <FormControl size="small" fullWidth>
          <InputLabel id={`st-${member.user_id}`}>Status</InputLabel>
          <Select
            labelId={`st-${member.user_id}`}
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {CHANNEL_MEMBER_STATUSES.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ minWidth: 130, verticalAlign: "middle" }}>
        <FormControl size="small" fullWidth>
          <InputLabel id={`rl-${member.user_id}`}>Role</InputLabel>
          <Select
            labelId={`rl-${member.user_id}`}
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {CHANNEL_MEMBER_ROLES.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
        <Typography variant="body2" color="text.secondary">
          {formatChannelDate(member.joined_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
        <Typography variant="body2" color="text.secondary">
          {formatChannelDate(member.last_posted_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
        <Typography variant="body2" color="text.secondary">
          {formatChannelDate(member.last_heard_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
        <Typography variant="body2" color="text.secondary">
          {formatChannelDate(member.status_changed_at)}
        </Typography>
      </TableCell>
      <TableCell sx={{ verticalAlign: "middle" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleApply}
          disabled={loading || !hasChanges}
        >
          Apply
        </Button>
      </TableCell>
    </TableRow>
  );
};

const ChannelMembersEditorTable = () => {
  const dataProvider = useDataProvider();
  const record = useRecordContext();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [searchText, setSearchText] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [roleFilter, setRoleFilter] = React.useState("all");
  const [userDirectory, setUserDirectory] = React.useState({});

  const members = React.useMemo(() => {
    const users = Array.isArray(record?.users) ? record.users : [];
    return [...users];
  }, [record?.users]);

  const memberUserIds = React.useMemo(
    () =>
      Array.from(new Set(members.map((member) => member.user_id).filter(Boolean))),
    [members]
  );
  const memberUserIdsKey = React.useMemo(() => memberUserIds.join("|"), [memberUserIds]);

  React.useEffect(() => {
    let active = true;

    if (memberUserIds.length === 0) {
      setUserDirectory({});
      return () => {
        active = false;
      };
    }

    const fetchUsers = async () => {
      try {
        const { data } = await dataProvider.getMany("users", { ids: memberUserIds });
        if (!active) return;
        const mapped = (data || []).reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});
        setUserDirectory(mapped);
      } catch (_err) {
        if (!active) return;
        setUserDirectory({});
      }
    };

    fetchUsers();

    return () => {
      active = false;
    };
  }, [dataProvider, memberUserIdsKey, memberUserIds]);

  const filteredAndSorted = React.useMemo(() => {
    const search = normalizeSortValue(searchText);
    return members
      .filter((member) => {
        const role = normalizeMemberRole(member.role);
        const status = normalizeMemberStatus(member.status);
        if (roleFilter !== "all" && role !== roleFilter) return false;
        if (statusFilter !== "all" && status !== statusFilter) return false;

        if (!search) return true;
        const user = userDirectory[member.user_id] || {};
        const firstName = normalizeSortValue(user.first_name || member.first_name);
        const lastName = normalizeSortValue(user.last_name || member.last_name);
        const fullName = `${firstName} ${lastName}`.trim();
        const userId = normalizeSortValue(member.user_id);
        return fullName.includes(search) || userId.includes(search);
      })
      .sort((a, b) => {
        const userA = userDirectory[a.user_id] || {};
        const userB = userDirectory[b.user_id] || {};

        const aLast = normalizeSortValue(userA.last_name || a.last_name);
        const bLast = normalizeSortValue(userB.last_name || b.last_name);
        if (aLast !== bLast) return aLast.localeCompare(bLast);

        const aFirst = normalizeSortValue(userA.first_name || a.first_name);
        const bFirst = normalizeSortValue(userB.first_name || b.first_name);
        if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

        return normalizeSortValue(a.user_id).localeCompare(
          normalizeSortValue(b.user_id)
        );
      });
  }, [members, searchText, roleFilter, statusFilter, userDirectory]);

  React.useEffect(() => {
    setPage(0);
  }, [searchText, statusFilter, roleFilter]);

  const pagedUsers = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSorted.slice(start, start + rowsPerPage);
  }, [filteredAndSorted, page, rowsPerPage]);

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredAndSorted.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredAndSorted.length, rowsPerPage, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!members.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No members on this conversation.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Edits use the admin API. Change status or role, then Apply (e.g. set status to
        Removed from channel to remove someone from the conversation).
      </Typography>
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <MUITextField
          size="small"
          label="Filter by name or user id"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="channel-users-filter-status">Status</InputLabel>
          <Select
            labelId="channel-users-filter-status"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All statuses</MenuItem>
            {CHANNEL_MEMBER_STATUSES.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="channel-users-filter-role">Role</InputLabel>
          <Select
            labelId="channel-users-filter-role"
            label="Role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <MenuItem value="all">All roles</MenuItem>
            {CHANNEL_MEMBER_ROLES.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Joined</TableCell>
            <TableCell>Last posted</TableCell>
            <TableCell>Last heard</TableCell>
            <TableCell>Status changed</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedUsers.map((member) => (
            <ChannelMemberEditorRow
              key={member.user_id}
              channelId={record.id}
              member={member}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredAndSorted.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </TableContainer>
  );
};

// In Ra -> Define filters that you randomly feed to List component filters prop

const channelFilters = [
<TextInput label="Conversation ID" source="_id" alwaysOn />
];

const AvatarField = ({ size = "25", sx }) => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Avatar
      src={`${record.image_url}?size=${size}x${size}`}
      style={{ width: parseInt(size, 10), height: parseInt(size, 10) }}
      variant="rounded"
      sx={sx}
    />
  );
};

export const FullNameField = (props) => {
  const { size } = props;
  const record = useRecordContext();
  const userCount = Array.isArray(record?.users) ? record.users.length : 0;
  return record ? (
    <Typography
      variant="body2"
      display="flex"
      flexWrap="nowrap"
      alignItems="center"
      component="div"
      sx={props.sx}
    >
      <AvatarField
        record={record}
        size={size}
        sx={{
          mr: 1,
          mt: -0.5,
          mb: -0.5,
        }}
      />
      {record.name} ({userCount})
    </Typography>
  ) : null;
};

export const ChannelLinkField = (props) => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }
  return (
    <Link to={`/channels/${record.id}`}>
      <FullNameField />
    </Link>
  );
};

// Call from Users to link back to channels.
export const ChannelLinkIdField = (props) => {
  const record = useRecordContext();
  return (
    <Link to={`/channels/${record.channel_id}`}>
      <TextField label={props.label} source="channel_id" />
    </Link>
  );

};

export const ChannelList = (props) => (
  <List {...props} filters={channelFilters}>
    <Datagrid rowClick="edit">
      <AvatarField />
      <TextField source="name" />
      <TextField source="description" />
      <TextField source="dm_hash" />
      <ReferenceField source="workspace_id" reference="workspaces">
        <WorkspaceLinkField />
      </ReferenceField>
      <ReferenceField source="owner_id" reference="users">
        <UserLinkField />
      </ReferenceField>
      <ReferenceField source="created_by" reference="users">
        <UserLinkField />
      </ReferenceField>
      <TextField source="visibility" />
      <ChannelUsersField />
      <DateField source="created_at" showTime />
      <DateField source="deleted_at" showTime />
    </Datagrid>
  </List>
);

const ChannelUsersField = () => {
  const record = useRecordContext();
  const users = Array.isArray(record?.users) ? record.users : [];
  const userCount = users.length;
  const maxAvatars = 10;

  if (userCount > maxAvatars) {
    return <Typography variant="body2">{userCount} users</Typography>;
  }

  return (
    <ArrayField source="users">
      <Box p={1}>
        <SingleFieldList>
          <ReferenceField source="user_id" reference="users">
            <Box m={0.25}>
              <UserAvatarField size={16} />
            </Box>
          </ReferenceField>
        </SingleFieldList>
      </Box>
    </ArrayField>
  );
};

const CHANNEL_VISIBILITIES = [
  { id: "private", name: "Private" },
  { id: "workspace", name: "Workspace" },
  { id: "public", name: "Public" },
];

export const ChannelSettingsEditor = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const serverName = record?.name || "";
  const serverVisibility = record?.visibility || "";
  const [name, setName] = React.useState(serverName);
  const [visibility, setVisibility] = React.useState(serverVisibility);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setName(serverName);
    setVisibility(serverVisibility);
  }, [record?.id, serverName, serverVisibility]);

  if (!record) return null;

  // The API forces DMs to private and ignores an empty name.
  const isDm = Boolean(record.dm_hash);
  const trimmedName = name.trim();
  const nameDirty = trimmedName !== serverName;
  const visibilityDirty = visibility !== serverVisibility;
  const hasChanges = (nameDirty && trimmedName !== "") || visibilityDirty;

  const handleApply = async () => {
    const patch = {};
    if (nameDirty && trimmedName) patch.name = trimmedName;
    if (visibilityDirty) patch.visibility = visibility;
    if (Object.keys(patch).length === 0) return;
    setLoading(true);
    try {
      await dataProvider.updateChannel({ id: record.id, ...patch });
      notify("Conversation updated", { type: "success" });
      refresh();
    } catch (e) {
      notify(
        typeof e?.message === "string"
          ? e.message
          : "Failed to update conversation",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      <MUITextField
        size="small"
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ minWidth: 280 }}
      />
      <FormControl size="small" sx={{ minWidth: 170 }} disabled={isDm}>
        <InputLabel id="channel-visibility">Visibility</InputLabel>
        <Select
          labelId="channel-visibility"
          label="Visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          {CHANNEL_VISIBILITIES.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        onClick={handleApply}
        disabled={loading || !hasChanges}
      >
        Apply
      </Button>
    </Box>
  );
};

const ChannelTitle = () => {
  const record = useRecordContext();
  return record ? (
    <span>
      {record.name} ({record.id})
    </span>
  ) : null;
};

export const ChannelEdit = () => (
  <Edit title={<ChannelTitle />} actions={false}>
    <TabbedForm toolbar={false}>
      <FormTab label="Info">
        <Labeled label="ID">
          <TextField source="id" />
        </Labeled>
        <Labeled label="DM Hash">
          <TextField source="dm_hash" />
        </Labeled>
        <Labeled label="Is Private?">
          <BooleanField source="is_private" />
        </Labeled>
        <ChannelSettingsEditor />
      </FormTab>

      <FormTab label="Users">
        <ChannelMembersEditorTable />
      </FormTab>
    </TabbedForm>
  </Edit>
);
